// hooks/use-color-picker-position.ts
import { useCallback, useRef } from 'react';

interface PositionOptions {
  pickerWidth: number;
  pickerHeight?: number;
  offsetY?: number;
  offsetX?: number;
  strategy?: 'auto' | 'left' | 'right' | 'center';
}

interface PositionResult {
  top: number;
  left: number;
  strategy: 'left' | 'right' | 'center';
}

export const useColorPickerPosition = (options: PositionOptions) => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  const getPositionStrategy = useCallback((
    triggerRect: DOMRect, 
    gridIndex: number, 
    columnsPerRow: number = 3
  ): 'left' | 'right' | 'center' => {
    if (options.strategy && options.strategy !== 'auto') {
      return options.strategy;
    }
    
    // Calculate position based on grid layout
    const columnIndex = gridIndex % columnsPerRow;
    
    if (columnIndex === 0) return 'left';
    if (columnIndex === columnsPerRow - 1) return 'right';
    return 'center';
  }, [options.strategy]);
  
  const calculatePosition = useCallback((
    triggerRect: DOMRect,
    gridIndex: number,
    columnsPerRow: number = 3
  ): PositionResult => {
    const { pickerWidth, offsetY = -54, offsetX = 28 } = options;
    const strategy = getPositionStrategy(triggerRect, gridIndex, columnsPerRow);
    
    const top = triggerRect.y + offsetY;
    
    let left: number;
    switch (strategy) {
      case 'left':
        left = triggerRect.x + offsetX;
        break;
      case 'right':
        left = triggerRect.x + triggerRect.width - pickerWidth - offsetX;
        break;
      case 'center':
      default:
        left = triggerRect.x + (triggerRect.width / 2) - (pickerWidth / 2);
        break;
    }
    
    return { top, left, strategy };
  }, [options, getPositionStrategy]);
  
  const positionPicker = useCallback((
    triggerRect: DOMRect,
    gridIndex: number,
    columnsPerRow: number = 3
  ) => {
    if (!containerRef.current) return null;
    
    const position = calculatePosition(triggerRect, gridIndex, columnsPerRow);
    
    containerRef.current.style.top = `${position.top}px`;
    containerRef.current.style.left = `${position.left}px`;
    
    return position;
  }, [calculatePosition]);
  
  return {
    containerRef,
    positionPicker,
    calculatePosition
  };
};